import React, { createContext, useState, useEffect, useContext, useCallback, useMemo } from 'react';
import { UserContext } from './UserContext';
import { fetchFamilyDashboard, fetchChildAllowanceSettings, updateChildAllowanceSettings, fetchChildTotalBalance} from '../api';

export const FamilyContext = createContext();

export const FamilyProvider = ({ children }) => {
    const { user } = useContext(UserContext);
    const [familyData, setFamilyData] = useState({
        children: [],
        parents: [],
        familyId: null,
        familyName: '',
        subscriptionTier: '',
        currencyCode: '',
        timezone: '',
        loading: true
    });

    const [selectedChildName, setSelectedChildName] = useState(null);
    const [allowanceData, setAllowanceData] = useState({});


    const fetchFamilyData = useCallback(async () => {
        setFamilyData(prev => ({ ...prev, loading: true }));
        try {
            const response = await fetchFamilyDashboard(user.details.family_id);
            console.log("Full family dashboard response:", response);
            if (response && response.children && response.parents) {
                setFamilyData({ 
                    children: response.children, 
                    parents: response.parents,
                    familyId: response.familyId,
                    familyName: response.familyName,
                    subscriptionTier: response.subscriptionTier,
                    currencyCode: response.currencyCode,
                    timezone: response.timezone,
                    loading: false 
                });
            } else {
                console.error("Incomplete family data found in the response");
                setFamilyData(prev => ({ ...prev, children: [], parents: [], loading: false }));
            }
        } catch (error) {
            console.error("Failed to fetch family data", error);
            setFamilyData(prev => ({ ...prev, children: [], parents: [], loading: false }));
        }
    }, [user.details.family_id]);

    const fetchAllowanceData = useCallback(async (childId) => {
        try {
            const data = await fetchChildAllowanceSettings(childId);
            setAllowanceData(prev => ({...prev, [childId]: data}));
        } catch (error) {
            console.error("Failed to fetch allowance data", error);
        }
    }, []);

    const updateAllowanceData = useCallback(async (childId, newData) => {
        try {
            await updateChildAllowanceSettings(childId, newData);
            setAllowanceData(prev => ({...prev, [childId]: {...prev[childId], ...newData}}));
            await fetchFamilyData();
        } catch (error) {
            console.error("Failed to update allowance data", error);
            throw error;
        }
    }, [fetchFamilyData]);

    const selectChildByName = useCallback((childName) => {
        console.log("Selecting child by name:", childName);
        setSelectedChildName(childName);
    }, []);

    const selectedChild = useMemo(() => {
        console.log("Computing selectedChild for name:", selectedChildName);
        return familyData.children.find(c => c.name === selectedChildName) || null;
    }, [familyData.children, selectedChildName]);

    useEffect(() => {
        fetchFamilyData();
    }, [fetchFamilyData]);

    useEffect(() => {
        if (selectedChild) {
            fetchAllowanceData(selectedChild.childId);
        }
    }, [selectedChild, fetchAllowanceData]);

    useEffect(() => {
        console.log("FamilyContext - selectedChild updated:", selectedChild);
    }, [selectedChild]);

    const refreshChildTotalBalance = useCallback(async (childId) => {
        try {
          const data = await fetchChildTotalBalance(childId);
          setFamilyData(prevData => ({
            ...prevData,
            children: prevData.children.map(child => 
              child.childId === childId 
                ? { ...child, totalAccountsBalance: data.totalBalance }
                : child
            )
          }));
          return data.totalBalance;
        } catch (error) {
          console.error('Failed to refresh child total balance:', error);
          throw error;
        }
      }, []);

    const contextValue = useMemo(() => ({
    familyChildren: familyData.children,
    familyParents: familyData.parents,
    familyId: familyData.familyId,
    familyName: familyData.familyName,
    subscriptionTier: familyData.subscriptionTier,
    currencyCode: familyData.currencyCode,
    timezone: familyData.timezone,
    loading: familyData.loading,
    refetchFamilyData: fetchFamilyData,
    selectedChild,
    selectChildByName,
    selectedChildName,
    allowanceData,
    updateAllowanceData,
    fetchAllowanceData,
    refreshChildTotalBalance  // Add this line
}), [familyData, selectedChild, selectChildByName, selectedChildName, allowanceData, updateAllowanceData, fetchAllowanceData, fetchFamilyData, refreshChildTotalBalance]);

    return (
        <FamilyContext.Provider value={contextValue}>
            {children}
        </FamilyContext.Provider>
    );
};

export const useFamily = () => {
    const context = useContext(FamilyContext);
    if (context === undefined) {
        throw new Error('useFamily must be used within a FamilyProvider');
    }
    return context;
};